import { useState, useEffect, useRef } from "react";
import { storage, db, functions } from "../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import {
  collection, doc, addDoc, updateDoc, deleteDoc, deleteField, onSnapshot,
  query, where, serverTimestamp, arrayUnion, arrayRemove, runTransaction,
} from "firebase/firestore";
import { C } from "../styles";
import { stripPidPrefix } from "./SessionCreator";

const assignOgFileFn = httpsCallable(functions, "assignOgFile");
const detachOgImageFn = httpsCallable(functions, "detachOgImage");
const submitOgLocationFn = httpsCallable(functions, "submitOgLocation");
const submitOgPoolFn = httpsCallable(functions, "submitOgPool");

// Kept in lockstep with OG_SUBMIT_ENABLED in functions/index.js. Enabled
// 2026-08-09 after verifying TBD accepts/persists location_name live.
const OG_SUBMIT_ENABLED = true;

// New "recording bundle" flow (2026-08): clip/turn ASSIGNMENT moves to TBD.
// Creators just bulk-upload bundles (one video + its task# + grouped photos);
// annotators assign clips/turns in TBD. When true, a selected location renders
// BundleUploader instead of the clip/turn LocationDetail. Global switch — the
// old flow stays in code for rollback until cutover. The submit-pool handoff
// is HELD (BUNDLE_SUBMIT_ENABLED) until TBD publishes its intake contract; we
// must NOT invent the payload shape (the location_name/turn_count lesson).
// Cutover 2026-08-13: bundle flow live. Verified round-trip against TBD's
// /api/og/pools (POST 201, fields intact, task_number 1–8, gs:// artifacts
// persisted) with og_pool_intake_enabled=true. Old clip/turn flow stays in
// code for rollback (set both back to false + redeploy).
const OG_BUNDLE_FLOW = true;
const BUNDLE_SUBMIT_ENABLED = true;

const newInboxId = () =>
  `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

// ── Outdoor Guide (OG) ───────────────────────────────────────────────────────
// A different shape from the slot-based sessions: creators organise outdoor
// recordings as Location → Task (1..8) → Clip. A clip has 1 video and, based
// on its turn count (1–5), one reference image per turn assigned in order
// (turnImages map). Older clips carry a legacy flat `images` array instead.
// Clip IDs follow OG_P<pid>_Task<n>_C<xxx>, with clip numbers running globally
// per (creator, task) across all of that creator's locations. Per-location
// bulk submit to TBD (one task per clip) is held behind OG_SUBMIT_ENABLED.

const OG_PROJECT = "Outdoor Guide";
const TASK_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8];
const VIDEO_EXT = ["mp4", "mov", "mkv", "avi"];
const IMAGE_EXT = ["jpg", "jpeg", "png", "heic", "heif", "webp"];

const extOf = (name) => (name.split(".").pop() || "").toLowerCase();
const isVideoFile = (name) => VIDEO_EXT.includes(extOf(name));
const isImageFile = (name) => IMAGE_EXT.includes(extOf(name));
const isHeicName = (name) => ["heic", "heif"].includes(extOf(name || ""));
// A HEIC photo can't render in an <img> until the server generates its JPEG
// preview (previewUrl). Until then, show a "generating" placeholder instead of
// a broken thumbnail.
const heicPreviewPending = (img) =>
  isHeicName(img.originalName || img.storedName) && !img.previewUrl;
const pad3 = (n) => String(n).padStart(3, "0");
// Natural, case-insensitive sort by originalName so IMG_2 < IMG_10 and gaps
// are easy to spot.
const byName = (a, b) => String(a.originalName || "").localeCompare(
  String(b.originalName || ""), undefined, { numeric: true, sensitivity: "base" });

function fmtBytes(b) {
  if (!b) return "";
  if (b >= 1e9) return (b / 1e9).toFixed(1) + " GB";
  if (b >= 1e6) return (b / 1e6).toFixed(1) + " MB";
  if (b >= 1e3) return (b / 1e3).toFixed(0) + " KB";
  return b + " B";
}

// Shared atomic counter helper (clip / location numbering).
async function allocateCounter(counterId) {
  const counterRef = doc(db, "projects", OG_PROJECT, "counters", counterId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const next = snap.exists() ? (snap.data().next || 1) : 1;
    tx.set(counterRef, { next: next + 1 }, { merge: true });
    return next;
  });
}

// Clip numbers restart per (creator, location, task) — the location segment in
// the ID keeps them unique across locations.
const allocateClipNumber = (pidNum, locCode, taskNum) =>
  allocateCounter(`OG_P${pidNum}_${locCode}_Task${taskNum}`);

// Location codes are sequential per creator: L001, L002, …
const allocateLocationCode = async (pidNum) =>
  `L${pad3(await allocateCounter(`OG_P${pidNum}_LOC`))}`;

// Clip ID: OG_P<pid>_L<loc>_Task<n>_C<xxx>
const buildClipId = (pidNum, locCode, taskNum, clipNum) =>
  `OG_P${pidNum}_${locCode}_Task${taskNum}_C${pad3(clipNum)}`;

// A clip is complete when it has a video and every turn is resolved — either
// it has an image or it's explicitly marked "No image" (turnNoImage). Legacy
// clips (no turnCount) fall back to "at least one image".
const ogClipComplete = (c) => {
  if (!c || !c.video) return false;
  if (c.turnCount) {
    const noImg = c.turnNoImage || {};
    for (let t = 1; t <= c.turnCount; t++) {
      const k = String(t);
      if (!(c.turnImages || {})[k] && !noImg[k]) return false;
    }
    return true;
  }
  return (c.images || []).length > 0;
};

function uploadTo(path, file, onProgress) {
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(ref(storage, path), file);
    task.on(
      "state_changed",
      (snap) => onProgress && onProgress(
        Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
      ),
      reject,
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });
}

// ── Design tokens (scoped to Outdoor Guide) ──────────────────────────────────
// One spacing / radius / type system so the whole page reads as a single
// cohesive surface rather than a stack of ad-hoc boxes. Colours reuse the
// app's CSS-variable tokens (C / var(--*)) so light + dark themes and their
// contrast ratios are inherited automatically.
const SP = { 1: "4px", 2: "8px", 3: "12px", 4: "16px", 5: "20px", 6: "24px", 8: "32px", 10: "40px" };
const RAD = { sm: "8px", md: "10px", lg: "14px" };
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const eyebrow = { fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.text2, margin: 0 };

const BTN_VARIANTS = {
  primary: { background: C.primary, color: "#fff", border: "1px solid transparent" },
  success: { background: C.success, color: "#fff", border: "1px solid transparent" },
  neutral: { background: "var(--surface-2)", color: C.text1, border: "1px solid var(--border-2)" },
  ghost: { background: "transparent", color: C.text2, border: "1px solid transparent" },
  danger: { background: "transparent", color: C.danger, border: "1px solid var(--border-2)" },
};

// Single button primitive — consistent height, radius, weight, focus ring
// (via .og-btn CSS), loading spinner, and disabled treatment.
function Btn({ variant = "neutral", size = "md", loading = false, children, style, className, ...rest }) {
  const v = BTN_VARIANTS[variant] || BTN_VARIANTS.neutral;
  const disabled = rest.disabled || loading;
  const pad = size === "sm" ? "6px 12px" : "10px 16px";
  const fs = size === "sm" ? "12px" : "13px";
  return (
    <button
      {...rest}
      disabled={disabled}
      className={"og-btn og-btn--" + variant + (className ? " " + className : "")}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: SP[2], fontFamily: "inherit", fontSize: fs, fontWeight: 600, lineHeight: 1, padding: pad, borderRadius: RAD.sm, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap", ...v, ...style }}
    >
      {loading && <span className="og-spin" aria-hidden="true" style={{ width: "13px", height: "13px", borderRadius: "50%", border: "2px solid currentColor", borderTopColor: "transparent", flexShrink: 0 }} />}
      {children}
    </button>
  );
}

// Small square icon button (remove / discard / delete) — always labelled.
function IconBtn({ label, danger = false, style, ...rest }) {
  return (
    <button
      aria-label={label} title={label}
      {...rest}
      className={"og-btn og-iconbtn" + (danger ? " og-btn--danger" : "")}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "28px", height: "28px", padding: 0, borderRadius: RAD.sm, background: "transparent", border: "1px solid var(--border-2)", color: danger ? C.danger : C.text2, cursor: rest.disabled ? "wait" : "pointer", fontFamily: "inherit", fontSize: "14px", lineHeight: 1, flexShrink: 0, ...style }}
    />
  );
}

// A labelled state indicator: colour dot + text (never colour alone → a11y).
function Stat({ tone = "neutral", children }) {
  const dot = { ready: C.success, sent: C.blue, incomplete: C.warning, neutral: C.text3 }[tone] || C.text3;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: SP[2], fontSize: "12px", color: C.text2, fontWeight: 500 }}>
      <span aria-hidden="true" style={{ width: "7px", height: "7px", borderRadius: "50%", background: dot, flexShrink: 0 }} />
      {children}
    </span>
  );
}

// A small image thumbnail with graceful fallback. Chrome can't render HEIC
// (common from iPhones), so on any load error we fall back to the emoji +
// filename rather than showing a broken-image icon.
function ImgThumb({ url, previewUrl, name, size = 40, emojiOnlyFallback = false }) {
  const [failed, setFailed] = useState(false);
  // Browsers can't render HEIC inline; prefer the server-generated JPEG preview
  // when one exists, else fall back to the original (fine for jpg/png).
  const src = previewUrl || url;
  if (failed || !src) {
    if (emojiOnlyFallback) {
      return <span aria-hidden="true" title={name} style={{ fontSize: "14px" }}>🖼️</span>;
    }
    return (
      <span style={{ fontSize: "12px", color: C.text2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={name}>
        🖼️ {name}
      </span>
    );
  }
  return (
    <img
      src={src} alt={name} title={name} loading="lazy"
      onError={() => setFailed(true)}
      style={{ width: size, height: size, objectFit: "cover", borderRadius: RAD.sm, border: "1px solid var(--border-2)", background: "var(--surface-2)", flexShrink: 0, display: "block" }}
    />
  );
}

// Add-a-task control (Task N selector + Add) — one component, used at the top
// and bottom of the tasks list so the affordance is always reachable.
function AddTaskControl({ value, onChange, onAdd, options, compact = false }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: SP[2] }}>
      <label className="og-visually-hidden" htmlFor={compact ? "og-add-task-bottom" : "og-add-task-top"}>Add a task</label>
      <select
        id={compact ? "og-add-task-bottom" : "og-add-task-top"}
        value={value} onChange={onChange} className="filter-select og-focus"
        style={{ fontSize: "13px", background: "var(--surface)", border: "1px solid var(--border-2)", borderRadius: RAD.sm, color: value ? C.text1 : C.text3, padding: "9px 10px", cursor: "pointer", fontFamily: "inherit" }}
      >
        <option value="">Add a task…</option>
        {options.map((n) => <option key={n} value={n}>Task {n}</option>)}
      </select>
      <Btn variant="primary" size="sm" onClick={onAdd} disabled={!value} style={{ padding: "9px 16px" }}>Add</Btn>
    </div>
  );
}

// ── Clip card ────────────────────────────────────────────────────────────────
function ClipCard({ clip, locId, taskNum, pid, userName, readOnly, onAssign, assigning }) {
  const [videoProgress, setVideoProgress] = useState(null);
  const [imgProgress, setImgProgress] = useState(null);
  const [error, setError] = useState("");
  const [dropOver, setDropOver] = useState(false);
  const [uploadingTurn, setUploadingTurn] = useState(null);
  const videoRef = useRef();
  const imgRef = useRef();
  const turnInputRef = useRef();
  const activeTurnRef = useRef(null);

  const turnCount = clip.turnCount || 0;
  const turnImages = clip.turnImages || {};
  const turnNoImage = clip.turnNoImage || {};
  const isTurnMode = turnCount > 0;
  // "Empty" = neither an image nor a "No image" mark. Only these are drop
  // targets / count as unresolved.
  const emptyTurns = isTurnMode
    ? Array.from({ length: turnCount }, (_, i) => i + 1)
      .filter((t) => !turnImages[String(t)] && !turnNoImage[String(t)])
    : [];

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropOver(false);
    if (readOnly || !onAssign) return;
    try {
      const { items } = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (!items || !items.length) return;
      const images = items.filter((i) => i.type === "image");
      // A lone video dropped on the clip replaces this clip's video.
      if (items.length === 1 && items[0].type === "video") {
        onAssign([items[0]], taskNum, clip.clipId);
      }
      if (!images.length) return;
      if (isTurnMode) {
        // Fill empty turn slots in order with the dropped images.
        if (emptyTurns.length === 0) {
          setError("All turns already have an image. Remove one first, or add a turn.");
          return;
        }
        images.slice(0, emptyTurns.length).forEach((img, idx) =>
          onAssign([img], taskNum, clip.clipId, emptyTurns[idx]));
      } else {
        onAssign(images, taskNum, clip.clipId);
      }
    } catch (_) { /* not a pool drag */ }
  };

  const handleTurnDrop = (turnNum) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropOver(false);
    if (readOnly || !onAssign) return;
    try {
      const { items } = JSON.parse(e.dataTransfer.getData("text/plain"));
      const img = (items || []).find((i) => i.type === "image");
      if (img) onAssign([img], taskNum, clip.clipId, turnNum);
    } catch (_) { /* not a pool drag */ }
  };

  const clipPath = `tasks.${taskNum}.clips.${clip.clipId}`;
  const locRef = doc(db, "projects", OG_PROJECT, "locations", locId);
  const storageBase =
    `projects/${OG_PROJECT}/locations/${locId}/${clip.clipId}`;

  const uploadedBy = { pid, name: userName };

  const handleVideo = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    if (file.size === 0) {
      setError("That file is empty (0 bytes). If it lives in iCloud/Drive, download it first.");
      return;
    }
    if (!isVideoFile(file.name)) {
      setError(`Video must be one of: ${VIDEO_EXT.join(", ")}.`);
      return;
    }
    try {
      setVideoProgress(0);
      const renamed = `${clip.clipId}_video.${extOf(file.name)}`;
      const url = await uploadTo(`${storageBase}/${renamed}`, file, setVideoProgress);
      await updateDoc(locRef, {
        [`${clipPath}.video`]: {
          originalName: file.name, renamedName: renamed, url,
          sizeBytes: file.size, uploadedBy, uploadedAt: new Date().toISOString(),
        },
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setError("Upload failed: " + err.message);
    } finally {
      setVideoProgress(null);
    }
  };

  const handleImages = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = "";
    if (!files.length) return;
    setError("");
    const empty = files.filter((f) => f.size === 0);
    const bad = files.filter((f) => f.size > 0 && !isImageFile(f.name));
    const usable = files.filter((f) => f.size > 0 && isImageFile(f.name));
    if (empty.length || bad.length) {
      setError(
        [empty.length ? `${empty.length} empty file(s) skipped.` : "",
          bad.length ? `${bad.length} non-image file(s) skipped (allowed: ${IMAGE_EXT.join(", ")}).` : ""]
          .filter(Boolean).join(" ")
      );
    }
    if (!usable.length) return;
    try {
      let done = 0;
      const startIndex = (clip.images || []).length;
      for (let i = 0; i < usable.length; i++) {
        const file = usable[i];
        setImgProgress(Math.round((done / usable.length) * 100));
        const renamed = `${clip.clipId}_img${startIndex + i + 1}.${extOf(file.name)}`;
        const url = await uploadTo(`${storageBase}/${renamed}`, file);
        await updateDoc(locRef, {
          [`${clipPath}.images`]: arrayUnion({
            originalName: file.name, renamedName: renamed, url,
            sizeBytes: file.size, uploadedBy, uploadedAt: new Date().toISOString(),
          }),
          updatedAt: serverTimestamp(),
        });
        done++;
      }
      setImgProgress(100);
    } catch (err) {
      setError("Image upload failed: " + err.message);
    } finally {
      setImgProgress(null);
    }
  };

  // Removing an image detaches it from the clip and returns it to the
  // Unassigned pool. The server moves the storage object to a fresh _inbox
  // path so the pool item owns an independent object (re-assignable safely).
  const [removingImg, setRemovingImg] = useState(null);
  const removeImage = async (img) => {
    setRemovingImg(img.renamedName);
    try {
      await detachOgImageFn({
        project: OG_PROJECT, locationId: locId, taskNum,
        clipId: clip.clipId, renamedName: img.renamedName,
      });
    } catch (err) {
      alert("Could not remove image: " + (err.message || err));
    } finally {
      setRemovingImg(null);
    }
  };

  // ── Turn-based images (one image per turn, in order) ──
  const pickTurnImage = (turnNum) => {
    activeTurnRef.current = turnNum;
    turnInputRef.current.click();
  };
  const handleTurnImage = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    const turnNum = activeTurnRef.current;
    if (!file || !turnNum) return;
    setError("");
    if (file.size === 0) {
      setError("That file is empty (0 bytes). If it lives in iCloud/Drive, download it first.");
      return;
    }
    if (!isImageFile(file.name)) {
      setError(`Image must be one of: ${IMAGE_EXT.join(", ")}.`);
      return;
    }
    try {
      setUploadingTurn(turnNum);
      const renamed = `${clip.clipId}_turn${turnNum}.${extOf(file.name)}`;
      const url = await uploadTo(`${storageBase}/${renamed}`, file);
      await updateDoc(locRef, {
        [`${clipPath}.turnImages.${turnNum}`]: {
          originalName: file.name, renamedName: renamed, url,
          sizeBytes: file.size, uploadedBy, uploadedAt: new Date().toISOString(),
        },
        // Adding an image clears any "No image" mark — the two are exclusive.
        [`${clipPath}.turnNoImage.${turnNum}`]: deleteField(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setError("Upload failed: " + err.message);
    } finally {
      setUploadingTurn(null);
    }
  };
  // Mark a turn as intentionally having no image, so later turns stay aligned
  // to their turn numbers. Undo returns it to an open slot.
  const markNoImage = async (turnNum) => {
    await updateDoc(locRef, {
      [`${clipPath}.turnNoImage.${turnNum}`]: true,
      updatedAt: serverTimestamp(),
    });
  };
  const clearNoImage = async (turnNum) => {
    await updateDoc(locRef, {
      [`${clipPath}.turnNoImage.${turnNum}`]: deleteField(),
      updatedAt: serverTimestamp(),
    });
  };
  const removeTurnImage = async (turnNum, img) => {
    setRemovingImg(img.renamedName);
    try {
      await detachOgImageFn({
        project: OG_PROJECT, locationId: locId, taskNum,
        clipId: clip.clipId, renamedName: img.renamedName, turnNumber: turnNum,
      });
    } catch (err) {
      alert("Could not remove image: " + (err.message || err));
    } finally {
      setRemovingImg(null);
    }
  };
  const changeTurns = async (n) => {
    const next = Math.max(1, Math.min(5, n));
    for (let t = next + 1; t <= turnCount; t++) {
      if (turnImages[String(t)]) {
        alert(`Turn ${t} still has an image — remove it before reducing the turn count.`);
        return;
      }
    }
    await updateDoc(locRef, {
      [`${clipPath}.turnCount`]: next, updatedAt: serverTimestamp(),
    });
  };

  const removeClip = async () => {
    if (!window.confirm(`Remove clip ${clip.clipId}? Uploaded files are not deleted from storage.`)) return;
    await updateDoc(locRef, {
      [clipPath]: deleteField(),
      updatedAt: serverTimestamp(),
    });
  };

  // Submit just this clip to QA (server scopes submitOgLocation to one clipId).
  const [submittingClip, setSubmittingClip] = useState(false);
  const submitClip = async () => {
    if (!window.confirm(`Submit clip ${clip.clipId} to QA?`)) return;
    setError("");
    setSubmittingClip(true);
    try {
      const res = await submitOgLocationFn({
        project: OG_PROJECT, locationId: locId, clipId: clip.clipId,
      });
      const d = res.data || {};
      if (d.counts?.failed) {
        setError(`Submission failed: ${(d.failed || []).join("; ")}`);
      }
    } catch (err) {
      setError("Submission failed: " + (err.message || err));
    } finally {
      setSubmittingClip(false);
    }
  };

  const images = [...(clip.images || [])].sort(byName);
  const complete = ogClipComplete(clip);
  const status = clip.tbdTaskId
    ? { tone: "sent", label: "Submitted" }
    : complete
      ? { tone: "ready", label: "Ready" }
      : { tone: "incomplete", label: "Needs media" };

  return (
    <article
      onDragOver={readOnly ? undefined : (e) => { e.preventDefault(); setDropOver(true); }}
      onDragLeave={() => setDropOver(false)}
      onDrop={handleDrop}
      className={"og-clip" + (dropOver ? " og-drop--over" : "")}
      aria-busy={assigning ? true : undefined}
      style={{
        border: "1px solid var(--border-2)", borderRadius: RAD.md, padding: SP[4],
        background: "var(--card)", opacity: assigning ? 0.6 : 1, position: "relative",
      }}
    >
      {/* Clip header: id + status + remove */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: SP[2], marginBottom: SP[4] }}>
        <div style={{ minWidth: 0 }}>
          <h4 title={clip.clipId} style={{ margin: 0, fontSize: "12px", fontWeight: 700, fontFamily: MONO, color: C.text1, letterSpacing: "0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{clip.clipId}</h4>
          <div style={{ marginTop: SP[1] }}><Stat tone={status.tone}>{status.label}</Stat></div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: SP[2], flexShrink: 0 }}>
          {OG_SUBMIT_ENABLED && !readOnly && complete && !clip.tbdTaskId && (
            <Btn variant="success" size="sm" onClick={submitClip} loading={submittingClip}>Submit</Btn>
          )}
          {!readOnly && !clip.tbdTaskId && (
            <IconBtn label={`Remove clip ${clip.clipId}`} danger onClick={removeClip}>✕</IconBtn>
          )}
        </div>
      </div>

      {/* Video */}
      <div style={{ marginBottom: SP[4] }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: SP[2] }}>
          <span style={eyebrow}>Video</span>
          {clip.video && <span style={{ fontSize: "11px", color: C.text3 }}>{fmtBytes(clip.video.sizeBytes)}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: SP[3], border: "1px solid var(--border-2)", borderRadius: RAD.sm, padding: `${SP[2]} ${SP[3]}`, background: "var(--surface)" }}>
          <span aria-hidden="true" style={{ width: "40px", height: "40px", flexShrink: 0, borderRadius: RAD.sm, background: clip.video ? "rgba(16,185,129,0.12)" : "var(--surface-2)", border: "1px solid var(--border-2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>🎬</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            {clip.video ? (
              <a href={clip.video.url} target="_blank" rel="noreferrer" className="og-focus" style={{ fontSize: "12px", color: C.text1, textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={clip.video.originalName}>{clip.video.originalName}</a>
            ) : videoProgress !== null ? (
              <span aria-live="polite" style={{ fontSize: "12px", color: C.primary }}>Uploading… {videoProgress}%</span>
            ) : (
              <span style={{ fontSize: "12px", color: C.text2 }}>No video yet</span>
            )}
          </div>
          {!readOnly && videoProgress === null && (
            <>
              <input ref={videoRef} type="file" accept={VIDEO_EXT.map((e) => "." + e).join(",")} style={{ display: "none" }} onChange={handleVideo} />
              <Btn variant="neutral" size="sm" onClick={() => videoRef.current.click()}>{clip.video ? "Replace" : "Upload"}</Btn>
            </>
          )}
        </div>
      </div>

      {/* Turns (one image per turn, in order) or legacy flat images */}
      {isTurnMode ? (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SP[2], marginBottom: SP[3] }}>
            <span style={eyebrow}>Turns · {turnCount - emptyTurns.length}/{turnCount}</span>
            {!readOnly && (
              <div style={{ display: "flex", alignItems: "center", gap: SP[2] }}>
                <label htmlFor={`turns-${clip.clipId}`} style={{ fontSize: "11px", color: C.text3 }}>Turns</label>
                <select id={`turns-${clip.clipId}`} value={turnCount} onChange={(e) => changeTurns(Number(e.target.value))} className="filter-select og-focus" style={{ fontSize: "12px", background: "var(--surface)", border: "1px solid var(--border-2)", borderRadius: RAD.sm, color: C.text1, padding: "4px 8px", cursor: "pointer", fontFamily: "inherit" }}>
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
          </div>
          <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: SP[2] }}>
            {Array.from({ length: turnCount }, (_, i) => i + 1).map((t) => {
              const im = turnImages[String(t)];
              const noImg = !!turnNoImage[String(t)];
              // Marker colour: filled = green, no-image = muted, empty = accent.
              const markerBg = im ? C.success : "transparent";
              const markerColor = im ? "#fff" : noImg ? C.text3 : "var(--og-accent)";
              const markerBorder = im ? "none" : `1px solid ${noImg ? "var(--border-2)" : "rgba(99,102,241,0.5)"}`;
              const resolved = im || noImg;
              return (
                <li key={t}>
                  <div
                    onDragOver={readOnly ? undefined : (e) => e.preventDefault()}
                    onDrop={readOnly ? undefined : handleTurnDrop(t)}
                    style={{ display: "flex", alignItems: "center", gap: SP[3], border: resolved ? "1px solid var(--border-2)" : "1px dashed rgba(99,102,241,0.4)", borderRadius: RAD.sm, padding: SP[2], background: im ? "var(--surface)" : noImg ? "var(--surface-2)" : "var(--primary-soft)" }}
                  >
                    <span aria-hidden="true" style={{ width: "22px", height: "22px", flexShrink: 0, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: 700, color: markerColor, background: markerBg, border: markerBorder }}>{t}</span>
                    {im ? (
                      <>
                        <a href={im.url} target="_blank" rel="noreferrer" className="og-focus" style={{ display: "flex", alignItems: "center", gap: SP[2], textDecoration: "none", flex: 1, minWidth: 0 }} title={im.originalName}>
                          <ImgThumb url={im.url} name={im.originalName} />
                          <span style={{ fontSize: "11px", color: C.text3, whiteSpace: "nowrap" }}>{fmtBytes(im.sizeBytes)}</span>
                        </a>
                        {!readOnly && (
                          <IconBtn label={`Remove image from turn ${t}`} danger disabled={removingImg === im.renamedName} onClick={() => removeTurnImage(t, im)}>{removingImg === im.renamedName ? "…" : "✕"}</IconBtn>
                        )}
                      </>
                    ) : noImg ? (
                      <>
                        <span style={{ fontSize: "12px", color: C.text3, flex: 1, fontStyle: "italic" }}>No image</span>
                        {!readOnly && (
                          <Btn variant="ghost" size="sm" onClick={() => clearNoImage(t)}>Undo</Btn>
                        )}
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: "12px", color: C.text2, flex: 1 }}>{uploadingTurn === t ? "Uploading…" : "Drop an image here, or"}</span>
                        {!readOnly && uploadingTurn !== t && (
                          <>
                            <Btn variant="neutral" size="sm" onClick={() => pickTurnImage(t)}>Add image</Btn>
                            <Btn variant="ghost" size="sm" onClick={() => markNoImage(t)}>No image</Btn>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
          <input ref={turnInputRef} type="file" accept={IMAGE_EXT.map((e) => "." + e).join(",")} style={{ display: "none" }} onChange={handleTurnImage} />
        </div>
      ) : (
        <div>
          <div style={{ ...eyebrow, marginBottom: SP[2] }}>Images · {images.length}</div>
          {images.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: SP[1], marginBottom: SP[2] }}>
              {images.map((img, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: SP[2] }}>
                  <a href={img.url} target="_blank" rel="noreferrer" className="og-focus" style={{ display: "flex", alignItems: "center", gap: SP[2], textDecoration: "none", flex: 1, minWidth: 0 }} title={img.originalName}>
                    <ImgThumb url={img.url} name={img.originalName} />
                    <span style={{ fontSize: "11px", color: C.text3, whiteSpace: "nowrap" }}>{fmtBytes(img.sizeBytes)}</span>
                  </a>
                  {!readOnly && (
                    <IconBtn label={`Remove ${img.originalName}`} danger disabled={removingImg === img.renamedName} onClick={() => removeImage(img)}>{removingImg === img.renamedName ? "…" : "✕"}</IconBtn>
                  )}
                </div>
              ))}
            </div>
          )}
          {imgProgress !== null && <div aria-live="polite" style={{ fontSize: "12px", color: C.primary, marginBottom: SP[2] }}>Uploading images… {imgProgress}%</div>}
          {!readOnly && imgProgress === null && (
            <>
              <input ref={imgRef} type="file" accept={IMAGE_EXT.map((e) => "." + e).join(",")} multiple style={{ display: "none" }} onChange={handleImages} />
              <Btn variant="neutral" size="sm" onClick={() => imgRef.current.click()}>Add image(s)</Btn>
            </>
          )}
        </div>
      )}

      {error && <div role="alert" style={{ fontSize: "12px", color: C.danger, marginTop: SP[3] }}>{error}</div>}
    </article>
  );
}

// ── Task section ─────────────────────────────────────────────────────────────
function TaskSection({ taskNum, task, locId, locCode, pid, userName, readOnly, onAssign }) {
  const [adding, setAdding] = useState(false);
  const [dropOver, setDropOver] = useState(false);
  const pidNum = stripPidPrefix(pid);

  // Videos dropped on the task body (not on a clip) each create a new clip.
  const handleTaskDrop = (e) => {
    e.preventDefault();
    setDropOver(false);
    if (readOnly || !onAssign) return;
    try {
      const { items } = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (!items || !items.length) return;
      const videos = items.filter((i) => i.type === "video");
      if (!videos.length) {
        alert("Drop images onto a specific clip. Only videos create a new clip.");
        return;
      }
      onAssign(videos, taskNum, null);
    } catch (_) { /* not a pool drag */ }
  };
  const clips = Object.values(task.clips || {})
    .sort((a, b) => (a.clipNumber || 0) - (b.clipNumber || 0));

  const addClip = async () => {
    if (!locCode) {
      alert("This location has no location code yet — it can't take new clips.");
      return;
    }
    const raw = window.prompt(
      "How many turns does this clip have? (1–5)\n\n" +
      "You'll assign one image to each turn, in order. You can change this " +
      "later on the clip.", "1");
    if (raw === null) return; // cancelled
    const turnCount = Math.max(1, Math.min(5, parseInt(raw, 10) || 1));
    setAdding(true);
    try {
      const num = await allocateClipNumber(pidNum, locCode, taskNum);
      const clipId = buildClipId(pidNum, locCode, taskNum, num);
      const locRef = doc(db, "projects", OG_PROJECT, "locations", locId);
      await updateDoc(locRef, {
        [`tasks.${taskNum}.clips.${clipId}`]: {
          clipId, clipNumber: num, taskNumber: taskNum,
          video: null, images: [], turnCount, turnImages: {},
          createdAt: new Date().toISOString(), createdBy: { pid, name: userName },
        },
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      alert("Could not add clip: " + err.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <section
      aria-label={`Task ${taskNum}`}
      onDragOver={readOnly ? undefined : (e) => { e.preventDefault(); setDropOver(true); }}
      onDragLeave={() => setDropOver(false)}
      onDrop={handleTaskDrop}
      className={"og-task" + (dropOver ? " og-drop--over" : "")}
      style={{ borderRadius: RAD.lg, padding: `${SP[4]} 0`, borderTop: "1px solid var(--border)" }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SP[3], marginBottom: clips.length ? SP[4] : SP[2] }}>
        <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: C.text1, letterSpacing: "-0.01em", display: "flex", alignItems: "baseline", gap: SP[2] }}>
          Task {taskNum}
          <span style={{ fontSize: "12px", fontWeight: 500, color: C.text3 }}>{clips.length} clip{clips.length === 1 ? "" : "s"}</span>
        </h3>
        {!readOnly && (
          <Btn variant="neutral" size="sm" onClick={addClip} loading={adding}>{adding ? "Adding…" : "Add clip"}</Btn>
        )}
      </div>
      {clips.length === 0 ? (
        <p style={{ margin: 0, fontSize: "13px", color: C.text2 }}>No clips yet — drag a video here, or use “Add clip”.</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: SP[4] }}>
          {clips.map((clip) => (
            <ClipCard key={clip.clipId} clip={clip} locId={locId} taskNum={taskNum} pid={pid} userName={userName} readOnly={readOnly}
              onAssign={onAssign} />
          ))}
        </div>
      )}
      {!readOnly && clips.length > 0 && (
        <button onClick={addClip} disabled={adding} className="og-btn og-add-clip og-focus" style={{ marginTop: SP[4], width: "100%", fontSize: "13px", fontWeight: 600, color: "var(--og-accent)", background: "transparent", border: "1px dashed rgba(99,102,241,0.4)", borderRadius: RAD.sm, padding: SP[3], cursor: adding ? "wait" : "pointer", fontFamily: "inherit" }}>{adding ? "Adding…" : "+ Add another clip"}</button>
      )}
    </section>
  );
}

// ── Unassigned pool ──────────────────────────────────────────────────────────
function InboxPool({ inbox, assigning, readOnly, onDiscard, selected, onToggleSelect }) {
  // Full-name tooltip on hover/focus (chip names are truncated to fit). We
  // track the hovered item + its on-screen rect and render a position:fixed
  // tooltip so it can't be clipped by the pool's own scroll overflow. Kept
  // before the early return so the hook order stays stable.
  const [tip, setTip] = useState(null); // { name, top, left } | null
  const showTip = (e, item) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ name: item.originalName, top: r.top, left: r.left });
  };
  if (!inbox.length) return null;
  const sorted = [...inbox].sort(byName);

  // What gets dragged: if the grabbed item is part of a multi-selection, drag
  // the whole selection; otherwise just that one item.
  const dragPayload = (item) => {
    const set = selected.has(item.id) && selected.size > 1
      ? sorted.filter((x) => selected.has(x.id))
      : [item];
    return JSON.stringify({ items: set.map((x) => ({ id: x.id, type: x.type })) });
  };

  return (
    <section aria-label="Unassigned files" className="og-pool" style={{
      position: "sticky", top: SP[3], zIndex: 5,
      background: "var(--surface)", border: "1px solid var(--border)",
      borderRadius: RAD.lg, padding: SP[4], marginBottom: SP[6],
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: SP[2], marginBottom: SP[1] }}>
        <h2 style={{ ...eyebrow, fontSize: "12px" }}>Unassigned · {inbox.length}</h2>
        {selected.size > 1 && <span style={{ fontSize: "12px", color: C.primary, fontWeight: 600 }}>{selected.size} selected</span>}
      </div>
      <p style={{ fontSize: "12px", color: C.text2, margin: `0 0 ${SP[3]}` }}>
        Drag videos onto a task, images onto a clip. Click to select several, then drag them together.
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: SP[2], maxHeight: "184px", overflowY: "auto" }}>
        {sorted.map((item) => {
          const busy = assigning.has(item.id);
          const isSel = selected.has(item.id);
          const accent = item.type === "video" ? "99,102,241" : "16,185,129";
          const toggle = () => !readOnly && !busy && onToggleSelect(item.id);
          return (
            <div
              key={item.id}
              draggable={!readOnly && !busy}
              onDragStart={(e) => e.dataTransfer.setData("text/plain", dragPayload(item))}
              onClick={toggle}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
              onMouseEnter={(e) => showTip(e, item)}
              onMouseLeave={() => setTip(null)}
              onFocus={(e) => showTip(e, item)}
              onBlur={() => setTip(null)}
              role="button"
              tabIndex={readOnly ? -1 : 0}
              aria-pressed={isSel}
              aria-label={`${item.type} ${item.originalName}${isSel ? ", selected" : ""}`}
              className="og-chip og-focus"
              style={{
                position: "relative",
                display: "flex", alignItems: "center", gap: SP[2],
                background: isSel ? `rgba(${accent},0.22)` : "var(--surface-2)",
                border: `1px solid rgba(${accent},${isSel ? "0.7" : "0.28"})`,
                boxShadow: isSel ? `0 0 0 1px rgba(${accent},0.45)` : "none",
                borderRadius: RAD.sm, padding: `${SP[1]} ${SP[2]}`, cursor: readOnly ? "default" : "grab",
                fontSize: "12px", color: C.text1, maxWidth: "230px", opacity: busy ? 0.5 : 1,
              }}
            >
              {isSel ? (
                <span aria-hidden="true" style={{ color: C.primary, fontWeight: 700 }}>✓</span>
              ) : item.type === "video" ? (
                <span aria-hidden="true">🎬</span>
              ) : (
                <ImgThumb url={item.url} name={item.originalName} size={26} emojiOnlyFallback />
              )}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.originalName}</span>
              {busy ? <span style={{ color: C.text3 }}>…</span> : (!readOnly && (
                <IconBtn label={`Discard ${item.originalName}`} onClick={(e) => { e.stopPropagation(); onDiscard(item); }} style={{ width: "20px", height: "20px", border: "none", color: C.text3 }}>✕</IconBtn>
              ))}
            </div>
          );
        })}
      </div>
      {tip && (
        <div role="tooltip" style={{
          position: "fixed", top: tip.top - 8, left: tip.left,
          transform: "translateY(-100%)", zIndex: 1000,
          maxWidth: "360px", whiteSpace: "normal", wordBreak: "break-all",
          background: "var(--card)", color: C.text1, border: "1px solid var(--border-2)",
          borderRadius: RAD.sm, padding: `${SP[1]} ${SP[2]}`, fontSize: "12px", lineHeight: 1.4,
          boxShadow: "0 8px 24px rgba(0,0,0,0.18)", pointerEvents: "none",
        }}>
          {tip.name}
        </div>
      )}
    </section>
  );
}

// ── Location detail ──────────────────────────────────────────────────────────
function LocationDetail({ location, pid, userName, readOnly, onBack }) {
  const [addTaskNum, setAddTaskNum] = useState("");
  // Per-file upload progress: [{ name, type, progress, status }].
  const [uploadItems, setUploadItems] = useState([]);
  const isUploading = uploadItems.some(
    (it) => it.status === "pending" || it.status === "uploading");
  const [bulkError, setBulkError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [assigning, setAssigning] = useState(new Set());
  const [selected, setSelected] = useState(new Set()); // pool multi-select
  const bulkRef = useRef();

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const tasks = location.tasks || {};
  const inbox = location.inbox || [];
  const presentTaskNums = Object.keys(tasks).map(Number).sort((a, b) => a - b);
  const availableTaskNums = TASK_NUMBERS.filter((n) => !presentTaskNums.includes(n));
  const locRef = doc(db, "projects", OG_PROJECT, "locations", location.id);

  const addTask = async () => {
    const n = Number(addTaskNum);
    if (!n) return;
    await updateDoc(locRef, {
      [`tasks.${n}`]: { clips: {} }, updatedAt: serverTimestamp(),
    });
    setAddTaskNum("");
  };

  const bulkUpload = async (fileList) => {
    if (isUploading) return; // one batch at a time
    const files = Array.from(fileList);
    const skipped = [];
    const usable = [];
    for (const f of files) {
      if (f.size === 0) { skipped.push(`${f.name} (empty)`); continue; }
      if (!isVideoFile(f.name) && !isImageFile(f.name)) {
        skipped.push(`${f.name} (unsupported)`); continue;
      }
      usable.push(f);
    }
    setBulkError(skipped.length ? `Skipped: ${skipped.join(", ")}` : "");
    if (!usable.length) return;
    const setItem = (i, patch) => setUploadItems(
        (prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    setUploadItems(usable.map((f) => ({
      name: f.name, type: isVideoFile(f.name) ? "video" : "image",
      progress: 0, status: "pending",
    })));
    for (let i = 0; i < usable.length; i++) {
      const f = usable[i];
      const id = newInboxId();
      const type = isVideoFile(f.name) ? "video" : "image";
      const stagePath = `projects/${OG_PROJECT}/locations/${location.id}/_inbox/${id}_${f.name}`;
      setItem(i, { status: "uploading" });
      try {
        const url = await uploadTo(stagePath, f, (p) => setItem(i, { progress: p }));
        await updateDoc(locRef, {
          inbox: arrayUnion({
            id, originalName: f.name, type, stagePath, url,
            sizeBytes: f.size, uploadedAt: new Date().toISOString(),
          }),
          updatedAt: serverTimestamp(),
        });
        setItem(i, { status: "done", progress: 100 });
      } catch (err) {
        setItem(i, { status: "error" });
        setBulkError("Some files failed to upload — see the list below.");
      }
    }
    // Successful files now live in the pool; keep only failures visible.
    setUploadItems((prev) => prev.filter((it) => it.status === "error"));
  };

  // Assign one or more pool items to a task (clipId=null → each video makes a
  // clip) or a specific clip. Sequential so the server reads a fresh image
  // count between calls (avoids duplicate _imgN names).
  const assignFiles = async (items, taskNum, clipId, turnNumber) => {
    for (const it of items) {
      setAssigning((prev) => new Set(prev).add(it.id));
      try {
        await assignOgFileFn({
          project: OG_PROJECT, locationId: location.id,
          inboxItemId: it.id, taskNum, clipId: clipId || null,
          turnNumber: turnNumber || null,
        });
      } catch (err) {
        alert("Could not assign file: " + (err.message || err));
      } finally {
        setAssigning((prev) => { const n = new Set(prev); n.delete(it.id); return n; });
      }
    }
    setSelected(new Set());
  };

  const discardInboxItem = async (item) => {
    if (!window.confirm(`Remove "${item.originalName}" from the pool? (The uploaded file stays in storage.)`)) return;
    await updateDoc(locRef, { inbox: arrayRemove(item), updatedAt: serverTimestamp() });
  };

  // A clip is submittable once it has a video + at least one image and hasn't
  // been sent yet. Counted across every task for the per-location bulk submit.
  const clipStats = { ready: 0, sent: 0, incomplete: 0 };
  for (const n of presentTaskNums) {
    const clips = (tasks[n] || {}).clips || {};
    for (const id of Object.keys(clips)) {
      const c = clips[id];
      if (c.tbdTaskId) clipStats.sent++;
      else if (ogClipComplete(c)) clipStats.ready++;
      else clipStats.incomplete++;
    }
  }

  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  const submitLocation = async () => {
    if (clipStats.ready === 0) return;
    if (!window.confirm(
      `Submit ${clipStats.ready} completed clip` +
      `${clipStats.ready === 1 ? "" : "s"} from "${location.name}" to QA? ` +
      `Clips still missing a video or photo are skipped.`)) return;
    setSubmitting(true);
    setSubmitMsg("");
    try {
      const res = await submitOgLocationFn({
        project: OG_PROJECT, locationId: location.id,
      });
      const { counts } = res.data || {};
      setSubmitMsg(
        `Submitted ${counts?.submitted ?? 0} · skipped ` +
        `${counts?.skipped ?? 0} · failed ${counts?.failed ?? 0}.`);
    } catch (err) {
      setSubmitMsg("Submission failed: " + (err.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  const clipTotal = clipStats.ready + clipStats.sent + clipStats.incomplete;

  return (
    <div>
      <button onClick={onBack} className="og-back og-focus" style={{ display: "inline-flex", alignItems: "center", gap: SP[1], fontSize: "13px", fontWeight: 600, color: C.text2, background: "transparent", border: "none", cursor: "pointer", padding: `0 0 ${SP[4]}`, fontFamily: "inherit" }}>
        <span aria-hidden="true">←</span> All locations
      </button>

      {/* Identity header */}
      <header style={{ marginBottom: SP[6] }}>
        <div style={{ display: "flex", alignItems: "center", gap: SP[3], flexWrap: "wrap" }}>
          <h1 style={{ fontSize: "24px", fontWeight: 700, color: C.text1, margin: 0, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{location.name}</h1>
          {location.locationCode && (
            <span style={{ fontSize: "12px", fontWeight: 600, fontFamily: MONO, color: "var(--og-accent)", background: "var(--primary-soft)", border: "1px solid rgba(99,102,241,0.3)", padding: "3px 8px", borderRadius: RAD.sm }}>{location.locationCode}</span>
          )}
        </div>
        <p style={{ fontSize: "13px", color: C.text2, margin: `${SP[2]} 0 0` }}>{presentTaskNums.length} task{presentTaskNums.length === 1 ? "" : "s"} · {clipTotal} clip{clipTotal === 1 ? "" : "s"} · {inbox.length} unassigned file{inbox.length === 1 ? "" : "s"}</p>
        {!location.locationCode && (
          <p role="alert" style={{ fontSize: "12px", color: C.warning, margin: `${SP[2]} 0 0`, display: "flex", alignItems: "center", gap: SP[1] }}>
            <span aria-hidden="true">⚠</span> This location predates location codes — new clips can't be numbered until it's assigned one.
          </p>
        )}
      </header>

      {/* Primary action — submit to QA (the conversion target) */}
      {OG_SUBMIT_ENABLED && !readOnly && (
        <section aria-label="Submit to QA" className="og-actionbar" style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: RAD.lg, padding: SP[5], marginBottom: SP[6] }}>
          <div style={{ minWidth: "220px", flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: C.text1 }}>Submit this location to QA</h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: `${SP[1]} ${SP[4]}`, marginTop: SP[2] }}>
              <Stat tone="ready">{clipStats.ready} ready</Stat>
              <Stat tone="sent">{clipStats.sent} submitted</Stat>
              <Stat tone="incomplete">{clipStats.incomplete} incomplete</Stat>
            </div>
            <p aria-live="polite" style={{ margin: submitMsg ? `${SP[2]} 0 0` : 0, fontSize: "12px", color: C.text2 }}>{submitMsg}</p>
          </div>
          <div className="og-actionbar__cta">
            <Btn variant="success" onClick={submitLocation} loading={submitting} disabled={clipStats.ready === 0} style={{ width: "100%" }}>
              {submitting ? "Submitting…" : `Submit ${clipStats.ready} clip${clipStats.ready === 1 ? "" : "s"}`}
            </Btn>
          </div>
        </section>
      )}

      {/* Media intake: dropzone + unassigned pool */}
      {!readOnly && (
        <div style={{ marginBottom: SP[3] }}>
          <h2 style={{ ...eyebrow, fontSize: "12px", marginBottom: SP[3] }}>Add media</h2>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files?.length) bulkUpload(e.dataTransfer.files); }}
            className={"og-drop" + (dragOver ? " og-drop--over" : "")}
            style={{
              border: "1.5px dashed var(--border-2)", background: "var(--surface)",
              borderRadius: RAD.lg, padding: SP[6], marginBottom: SP[5], textAlign: "center",
            }}
          >
            <input ref={bulkRef} type="file" multiple accept={[...VIDEO_EXT, ...IMAGE_EXT].map((e) => "." + e).join(",")} style={{ display: "none" }} onChange={(e) => { bulkUpload(e.target.files); e.target.value = ""; }} />
            <p aria-live="polite" style={{ fontSize: "13px", color: C.text2, margin: `0 0 ${SP[3]}` }}>
              {isUploading
                ? `Uploading ${uploadItems.filter((it) => it.status === "done").length}/${uploadItems.length} — keep this tab open`
                : "Drop all your videos & photos here, or"}
            </p>
            {!isUploading && (
              <Btn variant="primary" onClick={() => bulkRef.current.click()}>Select files</Btn>
            )}

            {/* Per-file progress */}
            {uploadItems.length > 0 && (
              <div style={{ marginTop: SP[4], display: "flex", flexDirection: "column", gap: SP[2], textAlign: "left", maxHeight: "260px", overflowY: "auto" }}>
                {uploadItems.map((it, i) => (
                  <div key={i}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SP[3], marginBottom: SP[1] }}>
                      <span style={{ fontSize: "12px", color: C.text2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <span aria-hidden="true">{it.type === "video" ? "🎬" : "🖼️"}</span> {it.name}
                      </span>
                      <span style={{ fontSize: "11px", flexShrink: 0, fontWeight: 600, color: it.status === "error" ? C.danger : it.status === "done" ? C.success : C.text3 }}>
                        {it.status === "error" ? "failed" : it.status === "pending" ? "queued" : it.status === "done" ? "done" : `${it.progress}%`}
                      </span>
                    </div>
                    <div style={{ background: "var(--border-subtle)", borderRadius: "999px", height: "4px", overflow: "hidden" }}>
                      <div className="og-progress" style={{ width: `${it.status === "done" ? 100 : it.progress}%`, height: "100%", borderRadius: "999px", background: it.status === "error" ? C.danger : it.status === "done" ? C.success : C.primary }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {bulkError && <div role="alert" style={{ fontSize: "12px", color: C.warning, marginTop: SP[3] }}>{bulkError}</div>}
          </div>
        </div>
      )}

      {/* Unassigned pool */}
      <InboxPool inbox={inbox} assigning={assigning} readOnly={readOnly} onDiscard={discardInboxItem} selected={selected} onToggleSelect={toggleSelect} />

      {/* Tasks */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SP[3], marginBottom: SP[2] }}>
        <h2 style={{ ...eyebrow, fontSize: "12px" }}>Tasks</h2>
        {!readOnly && availableTaskNums.length > 0 && (
          <AddTaskControl value={addTaskNum} onChange={(e) => setAddTaskNum(e.target.value)} onAdd={addTask} options={availableTaskNums} />
        )}
      </div>

      {presentTaskNums.length === 0 ? (
        <div style={{ fontSize: "13px", color: C.text2, padding: SP[8], textAlign: "center", border: "1px dashed var(--border-2)", borderRadius: RAD.lg }}>No tasks yet — add Task 1–8 above, then drag files from the pool.</div>
      ) : (
        <div>
          {presentTaskNums.map((n) => (
            <TaskSection key={n} taskNum={n} task={tasks[n]} locId={location.id} locCode={location.locationCode} pid={pid} userName={userName} readOnly={readOnly} onAssign={assignFiles} />
          ))}
        </div>
      )}

      {/* Bottom Add-task control so it's reachable after a long list of clips */}
      {!readOnly && presentTaskNums.length > 0 && availableTaskNums.length > 0 && (
        <div style={{ marginTop: SP[5], paddingTop: SP[4], borderTop: "1px solid var(--border)" }}>
          <AddTaskControl value={addTaskNum} onChange={(e) => setAddTaskNum(e.target.value)} onAdd={addTask} options={availableTaskNums} compact />
        </div>
      )}
    </div>
  );
}

// ── Recording bundle (new flow) ──────────────────────────────────────────────
// One video + a task# (1–8) + its grouped reference photos. No turns, no clip
// id, no renaming — annotators do all that in TBD. Files upload raw (original
// names) under the location's _bundles/ path.
function BundleCard({ bundle, locId, pid, userName, readOnly }) {
  const [videoProgress, setVideoProgress] = useState(null);
  const [imgProgress, setImgProgress] = useState(null);
  const [error, setError] = useState("");
  const videoRef = useRef();
  const imgRef = useRef();

  const locRef = doc(db, "projects", OG_PROJECT, "locations", locId);
  const path = `bundles.${bundle.bundleId}`;
  const storageBase =
    `projects/${OG_PROJECT}/locations/${locId}/_bundles/${bundle.bundleId}`;
  const uploadedBy = { pid, name: userName };
  const photos = [...(bundle.photos || [])].sort(byName);
  const ready = !!bundle.video && bundle.taskNumber != null;
  // Once a bundle is submitted it's locked from edits: TBD's intake is
  // idempotent per external_id, so a changed video/task/photo would never
  // propagate. `locked` also covers viewer (readOnly) mode.
  const submittedLock = bundle.submitStatus === "submitted";
  const locked = readOnly || submittedLock;

  const setTask = async (n) => {
    await updateDoc(locRef, {
      [`${path}.taskNumber`]: n ? Number(n) : null,
      updatedAt: serverTimestamp(),
    });
  };

  const handleVideo = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    if (file.size === 0) {
      setError("That file is empty (0 bytes). If it lives in iCloud/Drive, download it first.");
      return;
    }
    if (!isVideoFile(file.name)) {
      setError(`Video must be one of: ${VIDEO_EXT.join(", ")}.`);
      return;
    }
    try {
      setVideoProgress(0);
      const stored = `${newInboxId()}_${file.name}`;
      const url = await uploadTo(`${storageBase}/${stored}`, file, setVideoProgress);
      await updateDoc(locRef, {
        [`${path}.video`]: {
          originalName: file.name, storedName: stored, url,
          sizeBytes: file.size, uploadedBy, uploadedAt: new Date().toISOString(),
        },
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setError("Upload failed: " + err.message);
    } finally {
      setVideoProgress(null);
    }
  };

  const handleImages = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = "";
    if (!files.length) return;
    setError("");
    const usable = files.filter((f) => f.size > 0 && isImageFile(f.name));
    const bad = files.length - usable.length;
    if (bad > 0) {
      setError(`${bad} file(s) skipped (empty or not an image; allowed: ${IMAGE_EXT.join(", ")}).`);
    }
    if (!usable.length) return;
    try {
      let done = 0;
      for (const file of usable) {
        setImgProgress(Math.round((done / usable.length) * 100));
        const stored = `${newInboxId()}_${file.name}`;
        const url = await uploadTo(`${storageBase}/${stored}`, file);
        await updateDoc(locRef, {
          [`${path}.photos`]: arrayUnion({
            originalName: file.name, storedName: stored, url,
            sizeBytes: file.size, uploadedBy, uploadedAt: new Date().toISOString(),
          }),
          updatedAt: serverTimestamp(),
        });
        done++;
      }
      setImgProgress(100);
    } catch (err) {
      setError("Image upload failed: " + err.message);
    } finally {
      setImgProgress(null);
    }
  };

  const removePhoto = async (photo) => {
    await updateDoc(locRef, {
      [`${path}.photos`]: arrayRemove(photo), updatedAt: serverTimestamp(),
    });
  };
  const removeBundle = async () => {
    if (!window.confirm("Remove this bundle? Uploaded files are not deleted from storage.")) return;
    await updateDoc(locRef, { [path]: deleteField(), updatedAt: serverTimestamp() });
  };

  const status = submittedLock
    ? { tone: "sent", label: "Submitted" }
    : ready
    ? { tone: "ready", label: "Ready" }
    : { tone: "incomplete", label: bundle.video ? "Pick a task" : "Needs video" };

  return (
    <article className="og-clip" style={{ border: "1px solid var(--border-2)", borderRadius: RAD.md, padding: SP[4], background: "var(--card)" }}>
      {/* Header: status + task selector + remove */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: SP[2], marginBottom: SP[4] }}>
        <div style={{ minWidth: 0 }}>
          <div style={eyebrow}>Recording bundle</div>
          <div style={{ marginTop: SP[1] }}><Stat tone={status.tone}>{status.label}</Stat></div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: SP[2], flexShrink: 0 }}>
          {!locked ? (
            <>
              <label htmlFor={`task-${bundle.bundleId}`} style={{ fontSize: "11px", color: C.text3 }}>Task</label>
              <select id={`task-${bundle.bundleId}`} value={bundle.taskNumber || ""} onChange={(e) => setTask(e.target.value)} className="filter-select og-focus" style={{ fontSize: "12px", background: "var(--surface)", border: `1px solid ${bundle.taskNumber ? "var(--border-2)" : "rgba(245,158,11,0.5)"}`, borderRadius: RAD.sm, color: bundle.taskNumber ? C.text1 : C.text3, padding: "4px 8px", cursor: "pointer", fontFamily: "inherit" }}>
                <option value="">Task…</option>
                {TASK_NUMBERS.map((n) => <option key={n} value={n}>Task {n}</option>)}
              </select>
              <IconBtn label="Remove bundle" danger onClick={removeBundle}>✕</IconBtn>
            </>
          ) : (
            bundle.taskNumber && <span style={{ fontSize: "12px", color: C.text2, fontWeight: 600 }}>Task {bundle.taskNumber}</span>
          )}
        </div>
      </div>

      {/* Video */}
      <div style={{ marginBottom: SP[4] }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: SP[2] }}>
          <span style={eyebrow}>Video</span>
          {bundle.video && <span style={{ fontSize: "11px", color: C.text3 }}>{fmtBytes(bundle.video.sizeBytes)}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: SP[3], border: "1px solid var(--border-2)", borderRadius: RAD.sm, padding: `${SP[2]} ${SP[3]}`, background: "var(--surface)" }}>
          <span aria-hidden="true" style={{ width: "40px", height: "40px", flexShrink: 0, borderRadius: RAD.sm, background: bundle.video ? "rgba(16,185,129,0.12)" : "var(--surface-2)", border: "1px solid var(--border-2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>🎬</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            {bundle.video ? (
              <a href={bundle.video.url} target="_blank" rel="noreferrer" className="og-focus" style={{ fontSize: "12px", color: C.text1, textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={bundle.video.originalName}>{bundle.video.originalName}</a>
            ) : videoProgress !== null ? (
              <span aria-live="polite" style={{ fontSize: "12px", color: C.primary }}>Uploading… {videoProgress}%</span>
            ) : (
              <span style={{ fontSize: "12px", color: C.text2 }}>No video yet</span>
            )}
          </div>
          {!locked && videoProgress === null && (
            <>
              <input ref={videoRef} type="file" accept={VIDEO_EXT.map((e) => "." + e).join(",")} style={{ display: "none" }} onChange={handleVideo} />
              <Btn variant="neutral" size="sm" onClick={() => videoRef.current.click()}>{bundle.video ? "Replace" : "Upload"}</Btn>
            </>
          )}
        </div>
      </div>

      {/* Reference photos — grouped, unordered (annotator assigns turns in TBD) */}
      <div>
        <div style={{ ...eyebrow, marginBottom: SP[2] }}>Reference photos · {photos.length}</div>
        {photos.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: SP[3], marginBottom: SP[2] }}>
            {photos.map((img, i) => (
              <div key={i} style={{ position: "relative" }}>
                {heicPreviewPending(img) ? (
                  <span title={`${img.originalName} — preview generating…`} aria-label={`${img.originalName}, preview generating`} style={{ width: 48, height: 48, borderRadius: RAD.sm, border: "1px solid var(--border-2)", background: "var(--surface-2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span className="ui-spin" aria-hidden="true" style={{ width: 16, height: 16, border: "2px solid var(--border-2)", borderTopColor: "var(--og-accent)", borderRadius: "50%", display: "block" }} />
                  </span>
                ) : (
                  <a href={img.url} target="_blank" rel="noreferrer" className="og-focus" title={img.originalName}>
                    <ImgThumb url={img.url} previewUrl={img.previewUrl} name={img.originalName} size={48} />
                  </a>
                )}
                {!locked && (
                  <IconBtn label={`Remove ${img.originalName}`} danger onClick={() => removePhoto(img)} style={{ position: "absolute", top: "-8px", right: "-8px", width: "20px", height: "20px", background: "var(--card)" }}>✕</IconBtn>
                )}
              </div>
            ))}
          </div>
        )}
        {imgProgress !== null && <div aria-live="polite" style={{ fontSize: "12px", color: C.primary, marginBottom: SP[2] }}>Uploading photos… {imgProgress}%</div>}
        {!locked && imgProgress === null && (
          <>
            <input ref={imgRef} type="file" accept={IMAGE_EXT.map((e) => "." + e).join(",")} multiple style={{ display: "none" }} onChange={handleImages} />
            <Btn variant="neutral" size="sm" onClick={() => imgRef.current.click()}>Add photos</Btn>
          </>
        )}
      </div>

      {error && <div role="alert" style={{ fontSize: "12px", color: C.danger, marginTop: SP[3] }}>{error}</div>}
    </article>
  );
}

// ── Bundle uploader (new flow location view) ─────────────────────────────────
function BundleUploader({ location, pid, userName, readOnly, onBack }) {
  const [adding, setAdding] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const locRef = doc(db, "projects", OG_PROJECT, "locations", location.id);
  const bundles = Object.values(location.bundles || {})
    .sort((a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || "")));
  const isComplete = (b) => b.video && b.taskNumber != null;
  const isSubmitted = (b) => b.submitStatus === "submitted";
  // "Ready" = complete AND not yet submitted — the button only acts on these,
  // so it disables once everything complete has been sent (submit is idempotent
  // per bundle, but re-sending already-submitted bundles is a pointless no-op).
  const readyCount = bundles.filter((b) => isComplete(b) && !isSubmitted(b)).length;
  const submittedCount = bundles.filter(isSubmitted).length;
  const incompleteCount = bundles.filter((b) => !isComplete(b)).length;

  const addBundle = async () => {
    setAdding(true);
    try {
      const id = newInboxId();
      await updateDoc(locRef, {
        [`bundles.${id}`]: {
          bundleId: id, taskNumber: null, video: null, photos: [],
          status: "pool", createdAt: new Date().toISOString(),
          createdBy: { pid, name: userName },
        },
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      alert("Could not add bundle: " + err.message);
    } finally {
      setAdding(false);
    }
  };

  // Posts this location's ready bundles as one unassigned pool via the
  // submitOgPool Cloud Function (server holds TBD auth + does the authoritative
  // Storage size check). The button stays disabled until BUNDLE_SUBMIT_ENABLED,
  // and the server mirrors that with OG_POOL_SUBMIT_ENABLED, so nothing fires
  // until both flags flip at cutover. Idempotent per bundle (external_id =
  // bundleId), so re-submitting after adding more bundles is safe.
  const submitPool = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitMsg("Submitting bundles to the workflow tool…");
    try {
      const res = await submitOgPoolFn({
        project: OG_PROJECT, locationId: location.id,
      });
      const { counts = {}, skipped = [], failed = [] } = res.data || {};
      const parts = [`${counts.submitted || 0} submitted`];
      if (counts.skipped) parts.push(`${counts.skipped} skipped`);
      if (counts.failed) parts.push(`${counts.failed} failed`);
      let msg = parts.join(" · ");
      const detail = [...skipped, ...failed];
      if (detail.length) msg += ` — ${detail.join("; ")}`;
      setSubmitMsg(msg);
    } catch (err) {
      setSubmitMsg("Submit failed: " + (err.message || String(err)));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <button onClick={onBack} className="og-back og-focus" style={{ display: "inline-flex", alignItems: "center", gap: SP[1], fontSize: "13px", fontWeight: 600, color: C.text2, background: "transparent", border: "none", cursor: "pointer", padding: `0 0 ${SP[4]}`, fontFamily: "inherit" }}>
        <span aria-hidden="true">←</span> All locations
      </button>

      <header style={{ marginBottom: SP[5] }}>
        <div style={{ display: "flex", alignItems: "center", gap: SP[3], flexWrap: "wrap" }}>
          <h1 style={{ fontSize: "24px", fontWeight: 700, color: C.text1, margin: 0, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{location.name}</h1>
          {location.locationCode && (
            <span style={{ fontSize: "12px", fontWeight: 600, fontFamily: MONO, color: "var(--og-accent)", background: "var(--primary-soft)", border: "1px solid rgba(99,102,241,0.3)", padding: "3px 8px", borderRadius: RAD.sm }}>{location.locationCode}</span>
          )}
        </div>
        <p style={{ fontSize: "13px", color: C.text2, margin: `${SP[2]} 0 0`, maxWidth: "70ch" }}>
          Upload each recording as a bundle — one video, its task, and its reference photos. An annotator groups the clips and assigns turns later, so you don't need to.
        </p>
      </header>

      {/* Submit pool (held until TBD's intake contract is ready) */}
      {!readOnly && (
        <section aria-label="Submit pool" className="ui-actionbar ui-card" style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: RAD.lg, padding: SP[5], marginBottom: SP[6] }}>
          <div style={{ minWidth: "220px", flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: C.text1 }}>Submit bundles to the workflow tool</h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: `${SP[1]} ${SP[4]}`, marginTop: SP[2] }}>
              <Stat tone="ready">{readyCount} ready</Stat>
              <Stat tone="incomplete">{incompleteCount} incomplete</Stat>
              {submittedCount > 0 && <Stat tone="sent">{submittedCount} submitted</Stat>}
            </div>
            <p aria-live="polite" style={{ margin: submitMsg ? `${SP[2]} 0 0` : 0, fontSize: "12px", color: C.text2 }}>{submitMsg}</p>
          </div>
          <div className="ui-actionbar__cta">
            <Btn variant="success" onClick={submitPool} loading={submitting} disabled={!BUNDLE_SUBMIT_ENABLED || readyCount === 0} style={{ width: "100%" }}>
              {!BUNDLE_SUBMIT_ENABLED ? "Submit (coming soon)" :
                readyCount > 0 ? `Submit ${readyCount} bundle${readyCount === 1 ? "" : "s"}` :
                  submittedCount > 0 ? "All submitted" : "Submit bundles"}
            </Btn>
          </div>
        </section>
      )}

      {/* Bundles */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SP[3], marginBottom: SP[3] }}>
        <h2 style={{ ...eyebrow, fontSize: "12px" }}>Recording bundles · {bundles.length}</h2>
        {!readOnly && <Btn variant="primary" size="sm" onClick={addBundle} loading={adding}>Add bundle</Btn>}
      </div>

      {bundles.length === 0 ? (
        <div style={{ fontSize: "13px", color: C.text2, padding: SP[8], textAlign: "center", border: "1px dashed var(--border-2)", borderRadius: RAD.lg }}>No bundles yet — add one, then upload its video, task, and photos.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: SP[4] }}>
          {bundles.map((b) => (
            <BundleCard key={b.bundleId} bundle={b} locId={location.id} pid={pid} userName={userName} readOnly={readOnly} />
          ))}
        </div>
      )}

      {!readOnly && bundles.length > 0 && (
        <div style={{ marginTop: SP[4] }}>
          <Btn variant="neutral" size="sm" onClick={addBundle} loading={adding}>+ Add another bundle</Btn>
        </div>
      )}
    </div>
  );
}

// ── Top-level Outdoor Guide ──────────────────────────────────────────────────
function OutdoorGuide({ pid, userName, readOnly = false }) {
  const [locations, setLocations] = useState([]);
  const [ready, setReady] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!pid) return undefined;
    const q = query(
      collection(db, "projects", OG_PROJECT, "locations"),
      where("creatorPid", "==", pid)
    );
    const unsub = onSnapshot(q, (snap) => {
      setLocations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setReady(true);
    }, () => setReady(true));
    return unsub;
  }, [pid]);

  const createLocation = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      // Each location gets a per-creator code (L001, L002, …) that becomes part
      // of every clip ID and filename under it.
      const locationCode = await allocateLocationCode(stripPidPrefix(pid));
      const docRef = await addDoc(collection(db, "projects", OG_PROJECT, "locations"), {
        name, locationCode, creatorPid: pid, creatorName: userName, tasks: {},
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      setNewName("");
      setSelectedId(docRef.id);
    } catch (err) {
      alert("Could not create location: " + err.message);
    } finally {
      setCreating(false);
    }
  };

  const deleteLocation = async (loc) => {
    if (!window.confirm(`Delete location "${loc.name}" and its task/clip records? Uploaded files remain in storage.`)) return;
    await deleteDoc(doc(db, "projects", OG_PROJECT, "locations", loc.id));
  };

  const selected = locations.find((l) => l.id === selectedId) || null;

  if (selected) {
    return (
      <div className="og-page" style={{ maxWidth: "1080px" }}>
        {OG_BUNDLE_FLOW ? (
          <BundleUploader location={selected} pid={pid} userName={userName} readOnly={readOnly} onBack={() => setSelectedId(null)} />
        ) : (
          <LocationDetail location={selected} pid={pid} userName={userName} readOnly={readOnly} onBack={() => setSelectedId(null)} />
        )}
      </div>
    );
  }

  return (
    <div className="og-page" style={{ maxWidth: "1080px" }}>
      <header style={{ marginBottom: SP[6] }}>
        <h1 style={{ fontSize: "24px", fontWeight: 700, color: C.text1, margin: 0, letterSpacing: "-0.02em", lineHeight: 1.2 }}>Outdoor Guide</h1>
        <p style={{ fontSize: "13px", color: C.text2, margin: `${SP[2]} 0 0`, maxWidth: "60ch" }}>Organise outdoor recordings by location → task → clip. Each clip holds one video and one or more images.</p>
      </header>

      {!readOnly && (
        <div style={{ display: "flex", gap: SP[2], marginBottom: SP[6], maxWidth: "480px" }}>
          <label className="og-visually-hidden" htmlFor="og-new-location">New location name</label>
          <input
            id="og-new-location"
            type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") createLocation(); }}
            placeholder="New location name (e.g. Central Park)"
            className="og-focus"
            style={{ flex: 1, fontSize: "13px", background: "var(--surface)", border: "1px solid var(--border-2)", borderRadius: RAD.sm, color: C.text1, padding: "10px 12px", outline: "none", fontFamily: "inherit" }}
          />
          <Btn variant="primary" onClick={createLocation} loading={creating} disabled={!newName.trim()}>{creating ? "Creating…" : "Create location"}</Btn>
        </div>
      )}

      {!ready ? (
        <div style={{ fontSize: "13px", color: C.text2 }}>Loading…</div>
      ) : locations.length === 0 ? (
        <div style={{ fontSize: "13px", color: C.text2, padding: SP[10], textAlign: "center", border: "1px dashed var(--border-2)", borderRadius: RAD.lg }}>No locations yet. Create one above to start adding tasks and clips.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: SP[4] }}>
          {locations.map((loc) => {
            const taskCount = Object.keys(loc.tasks || {}).length;
            let clipCount = 0;
            let readyCount = 0;
            let sentCount = 0;
            for (const t of Object.values(loc.tasks || {})) {
              for (const c of Object.values(t.clips || {})) {
                clipCount++;
                if (c.tbdTaskId) sentCount++;
                else if (ogClipComplete(c)) readyCount++;
              }
            }
            return (
              <div key={loc.id} className="og-loc-tile" style={{ position: "relative", border: "1px solid var(--border)", borderRadius: RAD.lg, background: "var(--card)" }}>
                <button onClick={() => setSelectedId(loc.id)} className="og-loc-open og-focus" style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: "pointer", padding: SP[5], fontFamily: "inherit", borderRadius: RAD.lg }}>
                  <div style={{ fontSize: "15px", fontWeight: 700, color: C.text1, letterSpacing: "-0.01em", marginBottom: SP[3], paddingRight: readOnly ? 0 : "56px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{loc.name}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: `${SP[1]} ${SP[3]}` }}>
                    <span style={{ fontSize: "12px", color: C.text3 }}>{taskCount} task{taskCount === 1 ? "" : "s"} · {clipCount} clip{clipCount === 1 ? "" : "s"}</span>
                  </div>
                  {(readyCount > 0 || sentCount > 0) && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: `${SP[1]} ${SP[3]}`, marginTop: SP[2] }}>
                      {readyCount > 0 && <Stat tone="ready">{readyCount} ready</Stat>}
                      {sentCount > 0 && <Stat tone="sent">{sentCount} submitted</Stat>}
                    </div>
                  )}
                </button>
                {!readOnly && (
                  <div style={{ position: "absolute", top: SP[3], right: SP[3] }}>
                    <Btn variant="danger" size="sm" onClick={() => deleteLocation(loc)} aria-label={`Delete location ${loc.name}`}>Delete</Btn>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default OutdoorGuide;
